import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError, inr, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowsClockwise, Archive, Trash } from "@phosphor-icons/react";

export default function AssetDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const canEdit = ["founder", "accountant"].includes(user?.role);
    const canDelete = user?.role === "founder";
    const [asset, setAsset] = useState(null);
    const [ledgers, setLedgers] = useState([]);
    const [disposeOpen, setDisposeOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            const [a, l] = await Promise.all([
                api.get(`/assets/${id}`),
                api.get(`/ledgers`),
            ]);
            setAsset(a.data); setLedgers(l.data);
        } catch (e) { toast.error(formatApiError(e)); }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    const remove = async () => {
        if (!window.confirm("Delete this asset? Only possible if no depreciation has been posted.")) return;
        try {
            await api.delete(`/assets/${id}`);
            toast.success("Asset deleted");
            nav("/assets");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (!asset) return <div className="p-8 text-stone-500">Loading…</div>;

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title={asset.name}
                sub={<>{asset.category_name} · {asset.wdv_rate_pct}% WDV p.a. · <span className="text-stone-500">{asset.description || ""}</span></>}
                right={
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={load} data-testid="asset-refresh">
                            <ArrowsClockwise size={14} className="mr-1" /> Refresh
                        </Button>
                        {canEdit && asset.status === "active" ? (
                            <Button variant="outline" onClick={() => setDisposeOpen(true)} data-testid="asset-dispose-btn">
                                <Archive size={14} className="mr-2" /> Sell / dispose
                            </Button>
                        ) : null}
                        {canDelete && asset.status === "active" ? (
                            <Button variant="outline" onClick={remove} data-testid="asset-delete-btn">
                                <Trash size={14} className="mr-2" /> Delete
                            </Button>
                        ) : null}
                        <Button variant="outline" onClick={() => nav("/assets")}>Back</Button>
                    </div>
                }
            />

            <div className="p-8 space-y-6 max-w-5xl">
                <div className="grid gap-4 md:grid-cols-3">
                    <Tile label="Original cost (depreciable base)" value={asset.opening_wdv} rupee />
                    <Tile label="Accumulated depreciation" value={asset.accumulated_depreciation} rupee tone="rose" />
                    <Tile label="Current book value (WDV)" value={asset.current_wdv} rupee tone="teal" bold />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                    <Card title="Asset details">
                        <KV label="Category">
                            {asset.category_name}
                            {asset.category_rate_pct != null && asset.category_rate_pct !== asset.wdv_rate_pct
                                ? <span className="text-[10px] text-stone-500 ml-1">(cat default {asset.category_rate_pct}% · override {asset.wdv_rate_pct}%)</span>
                                : null}
                        </KV>
                        <KV label="Put to use">{formatDate(asset.put_to_use_date)}</KV>
                        <KV label="Location · Department">{asset.location || "—"} · {asset.department || "—"}</KV>
                        <KV label="Status">{asset.status}</KV>
                        {asset.status !== "active" ? (
                            <>
                                <KV label="Sold / disposed on">{formatDate(asset.sold_on || "")}</KV>
                                <KV label="Sale value">₹ {inr(asset.sold_amount || 0)}</KV>
                                <KV label="Gain / loss on sale">
                                    <span style={{ color: (asset.disposal_gain_loss || 0) >= 0 ? "#059669" : "#dc2626" }}>
                                        {(asset.disposal_gain_loss || 0) >= 0 ? "+" : "−"} ₹ {inr(Math.abs(asset.disposal_gain_loss || 0))}
                                    </span>
                                </KV>
                                {asset.disposal_notes ? <KV label="Notes">{asset.disposal_notes}</KV> : null}
                            </>
                        ) : null}
                    </Card>
                    <Card title="Cost + GST">
                        <KV label="Taxable value (ex-GST)">₹ {inr(asset.taxable_value)}</KV>
                        <KV label="GST amount">₹ {inr(asset.gst_amount)}</KV>
                        <KV label="Gross invoice value">₹ {inr(asset.gross_invoice_value)}</KV>
                        <KV label="ITC claimed?">
                            {asset.itc_claimed
                                ? <span className="text-emerald-700 text-xs">Yes · depreciation on taxable value</span>
                                : <span className="text-amber-700 text-xs">No (blocked) · GST capitalised, dep on gross</span>}
                        </KV>
                        <KV label="Depreciable base">₹ {inr(asset.depreciable_base)}</KV>
                    </Card>
                </div>

                <Card title="Depreciation history">
                    {asset.depreciation_history && asset.depreciation_history.length > 0 ? (
                        <table className="se-table text-sm" data-testid="asset-dep-history">
                            <thead>
                                <tr><th>Date</th><th>Narration</th><th className="text-right">Amount</th></tr>
                            </thead>
                            <tbody>
                                {asset.depreciation_history.map((j) => {
                                    // narration like "Depreciation — <Name> (YYYY-MM)"; extract month for a drill link
                                    const monthMatch = /\((\d{4}-\d{2})\)$/.exec(j.narration || "");
                                    const m = monthMatch ? monthMatch[1] : null;
                                    return (
                                        <tr key={j.id} data-testid={`dep-hist-${j.id}`}>
                                            <td className="text-xs">{formatDate(j.date)}</td>
                                            <td className="text-sm">{j.narration}</td>
                                            <td className="se-num text-right text-rose-700">
                                                {m ? <Link to={`/reports/monthly-pnl?month=${m}`} className="hover:underline">₹ …</Link>
                                                   : "₹ …"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <div className="text-sm text-stone-500 py-6 text-center">No depreciation posted yet. Use <b>Close month depreciation</b> on the Assets page to generate it.</div>
                    )}
                </Card>

                {asset.notes ? (
                    <Card title="Notes">
                        <div className="text-sm text-stone-700 whitespace-pre-wrap">{asset.notes}</div>
                    </Card>
                ) : null}
            </div>

            <DisposeDialog open={disposeOpen} onOpenChange={setDisposeOpen} asset={asset} ledgers={ledgers} onSaved={load} />
        </>
    );
}

function Tile({ label, value, rupee, tone, bold }) {
    const color = tone === "rose" ? "#dc2626" : tone === "teal" ? "#0d9488" : "#0f172a";
    return (
        <div className="border rounded-md px-5 py-4 bg-white" style={{ borderColor: "var(--se-border)" }} data-testid={`tile-${label}`}>
            <div className="text-[11px] uppercase tracking-widest text-stone-500">{label}</div>
            <div className={`se-num mt-1 ${bold ? "text-3xl font-medium" : "text-2xl"}`} style={{ color }}>
                {rupee ? "₹ " : ""}{inr(value || 0)}
            </div>
        </div>
    );
}
function Card({ title, children }) {
    return (
        <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
            <div className="px-5 py-3 border-b text-xs uppercase tracking-widest" style={{ borderColor: "var(--se-border)", color: "var(--se-plum)" }}>
                {title}
            </div>
            <div className="p-5 space-y-2">{children}</div>
        </div>
    );
}
function KV({ label, children }) {
    return (
        <div className="flex items-start gap-4 text-sm">
            <div className="text-stone-500 min-w-[180px]">{label}</div>
            <div className="font-medium text-stone-800">{children}</div>
        </div>
    );
}

function DisposeDialog({ open, onOpenChange, asset, ledgers, onSaved }) {
    const [f, setF] = useState({ sold_on: new Date().toISOString().slice(0, 10), sold_amount: "", receiving_ledger_id: "", notes: "" });
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (open) setF({ sold_on: new Date().toISOString().slice(0, 10), sold_amount: "", receiving_ledger_id: "", notes: "" });
    }, [open]);
    const bankLedgers = (ledgers || []).filter((l) => l.party_type === "bank" || l.party_type === "cash");
    const soldAmount = Number(f.sold_amount || 0);
    const gainLoss = soldAmount - (asset?.current_wdv || 0);

    const save = async () => {
        if (!f.sold_on) return toast.error("Sale date required");
        if (soldAmount > 0 && !f.receiving_ledger_id) return toast.error("Choose the bank/cash receiving the sale value");
        setSaving(true);
        try {
            const { data } = await api.post(`/assets/${asset.id}/dispose`, {
                sold_on: f.sold_on,
                sold_amount: soldAmount,
                receiving_ledger_id: f.receiving_ledger_id || null,
                notes: f.notes || null,
            });
            toast.success(`Asset ${soldAmount > 0 ? "sold" : "written off"} · gain/loss ₹${inr(data.gain_loss)}`);
            onOpenChange(false);
            onSaved?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="dispose-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Sell / dispose asset</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="text-sm text-stone-600 bg-stone-50 border rounded-sm p-3">
                        Current book value (WDV): <b>₹ {inr(asset?.current_wdv || 0)}</b>. Any accumulated depreciation and the original cost are cleared from the balance sheet; gain/loss on sale posts to P&amp;L.
                    </div>
                    <div>
                        <Label>Sale date *</Label>
                        <Input type="date" value={f.sold_on} onChange={(e) => setF({ ...f, sold_on: e.target.value })} data-testid="dispose-date" />
                    </div>
                    <div>
                        <Label>Sale amount (₹)</Label>
                        <Input type="number" value={f.sold_amount} onChange={(e) => setF({ ...f, sold_amount: e.target.value })} placeholder="0 = written off" data-testid="dispose-amount" />
                    </div>
                    {soldAmount > 0 ? (
                        <div>
                            <Label>Receiving bank / cash *</Label>
                            <Select value={f.receiving_ledger_id} onValueChange={(v) => setF({ ...f, receiving_ledger_id: v })}>
                                <SelectTrigger data-testid="dispose-bank"><SelectValue placeholder="Pick where the money came in" /></SelectTrigger>
                                <SelectContent>
                                    {bankLedgers.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    ) : null}
                    <div className="border rounded-sm p-3 text-sm" style={{ borderColor: "var(--se-border)", background: gainLoss >= 0 ? "#f0fdf4" : "#fef2f2" }}>
                        Sale ₹ {inr(soldAmount)} − WDV ₹ {inr(asset?.current_wdv || 0)} = {" "}
                        <b style={{ color: gainLoss >= 0 ? "#059669" : "#dc2626" }}>
                            {gainLoss >= 0 ? "Gain" : "Loss"} of ₹ {inr(Math.abs(gainLoss))}
                        </b>
                    </div>
                    <div>
                        <Label>Notes</Label>
                        <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Optional context" data-testid="dispose-notes" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={save} disabled={saving} style={{ background: "#dc2626", color: "#fff" }} data-testid="save-dispose">
                        {saving ? "Posting…" : "Confirm disposal"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
